import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { courseApi, learningPathApi } from '../../services/api';
import { StatCard, EmptyState, Spinner } from '../../components/shared/index';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  AcademicCapIcon,
  CheckBadgeIcon,
  PlayCircleIcon,
  ClockIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';

const STATUS_STYLES = {
  assigned:    'bg-gray-100   text-gray-700',
  in_progress: 'bg-amber-100  text-amber-800',
  completed:   'bg-green-100  text-green-800',
  failed:      'bg-red-100    text-red-800',
};

function StatusChip({ status }) {
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', STATUS_STYLES[status] || STATUS_STYLES.assigned)}>
      {status?.replace('_',' ')}
    </span>
  );
}

export default function MyLearning() {
  const qc = useQueryClient();

  const { data: enrollments = [], isLoading: loadingEnr } = useQuery({
    queryKey: ['my-enrollments'],
    queryFn:  () => courseApi.mine().then(r => r.data),
  });

  const { data: paths = [], isLoading: loadingPaths } = useQuery({
    queryKey: ['my-paths'],
    queryFn:  () => learningPathApi.mine().then(r => r.data),
  });

  const completeMut = useMutation({
    mutationFn: (eid) => courseApi.markComplete(eid, { status: 'completed' }),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: ['my-enrollments'] });
      qc.invalidateQueries({ queryKey: ['my-paths'] });
      toast.success('Course marked complete');
    },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to mark complete'),
  });

  const startMut = useMutation({
    mutationFn: (eid) => courseApi.markComplete(eid, { status: 'in_progress' }),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['my-enrollments'] }); toast.success('Started'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  if (loadingEnr || loadingPaths) {
    return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;
  }

  const total       = enrollments.length;
  const completed   = enrollments.filter(e => e.status === 'completed').length;
  const inProgress  = enrollments.filter(e => e.status === 'in_progress').length;
  const assigned    = enrollments.filter(e => e.status === 'assigned').length;
  const nextUp      = enrollments.find(e => e.status === 'in_progress') || enrollments.find(e => e.status === 'assigned');

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <StatCard title="Enrolments" value={total}     color="blue"   icon={AcademicCapIcon} />
        <StatCard title="Completed"  value={completed} color="green"  icon={CheckBadgeIcon} />
        <StatCard title="In progress" value={inProgress} color="amber" icon={PlayCircleIcon} />
        <StatCard title="Assigned"   value={assigned}  color="purple" icon={ClockIcon} />
      </div>

      {nextUp && (
        <div className="card p-5 border-l-4 border-blue-500 bg-blue-50/60">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Next up</p>
              <h3 className="text-lg font-semibold text-gray-900 mt-1">{nextUp.course?.title}</h3>
              <p className="text-sm text-gray-600 mt-1 line-clamp-2">{nextUp.course?.description || 'Resume where you left off.'}</p>
              <div className="flex items-center gap-3 mt-3 text-xs text-gray-500">
                <span className="capitalize">{nextUp.course?.content_type?.replace('_',' ')}</span>
                {nextUp.course?.duration_minutes && <span>{nextUp.course.duration_minutes} min</span>}
                <StatusChip status={nextUp.status} />
              </div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              {nextUp.course?.content_url && (
                <a
                  href={nextUp.course.content_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => nextUp.status === 'assigned' && startMut.mutate(nextUp.id)}
                  className="btn-primary"
                >
                  Open <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                </a>
              )}
              {nextUp.status !== 'completed' && (
                <button
                  onClick={() => completeMut.mutate(nextUp.id)}
                  disabled={completeMut.isPending}
                  className="btn-secondary"
                >
                  Mark complete
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <section>
        <h2 className="section-title mb-3">My courses</h2>
        {enrollments.length === 0 ? (
          <div className="card">
            <EmptyState
              title="No enrolments yet"
              description="Browse the course catalog to find courses aligned to your goals and competencies."
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {enrollments.map(e => (
              <div key={e.id} className="card p-4 flex flex-col">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">{e.course?.title}</h3>
                  <StatusChip status={e.status} />
                </div>
                <p className="text-xs text-gray-500 flex-1 line-clamp-3 mb-3">{e.course?.description}</p>
                <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                  <span className="capitalize">{e.course?.content_type?.replace('_',' ')}</span>
                  {e.course?.duration_minutes && <span>{e.course.duration_minutes} min</span>}
                </div>
                <div className="flex gap-2 mt-auto">
                  {e.course?.content_url && (
                    <a
                      href={e.course.content_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => e.status === 'assigned' && startMut.mutate(e.id)}
                      className="btn-secondary btn-sm flex-1 justify-center"
                    >
                      Open
                    </a>
                  )}
                  {e.status !== 'completed' && (
                    <button
                      onClick={() => completeMut.mutate(e.id)}
                      disabled={completeMut.isPending}
                      className="btn-primary btn-sm flex-1 justify-center"
                    >
                      Complete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="section-title mb-3">My learning paths</h2>
        {paths.length === 0 ? (
          <div className="card">
            <EmptyState
              title="No learning paths assigned"
              description="Once HR or your manager assigns you a path, it'll show here with per-course progress."
            />
          </div>
        ) : (
          <div className="space-y-3">
            {paths.map(p => (
              <div key={p.assignment_id} className="card p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">{p.name}</h3>
                    <p className="text-sm text-gray-500 mt-0.5">{p.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900">{p.progress_pct}%</p>
                    <p className="text-xs text-gray-500">complete</p>
                  </div>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5 mb-3">
                  <div className="bg-blue-600 h-1.5 rounded-full transition-all" style={{ width: `${p.progress_pct}%` }} />
                </div>
                <div className="divide-y divide-gray-100">
                  {p.items?.map(it => (
                    <div key={it.item_id} className="flex items-center justify-between py-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 w-6">#{it.order + 1}</span>
                        <span className="text-gray-800">{it.course?.title}</span>
                        {!it.is_required && <span className="text-xs text-gray-400">(optional)</span>}
                      </div>
                      <StatusChip status={it.status} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
